/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
/**
 * Permission constants matching the DB permission keys exactly.
 * These values align with what GET /auth/me returns in user.permissions[].
 */
export const PERMISSIONS = {
  // Account Management
  VIEW_ACCOUNTS: 'accounts.view',
  CREATE_ACCOUNT: 'accounts.create',
  UPDATE_ACCOUNT: 'accounts.update',
  DELETE_ACCOUNT: 'accounts.delete',

  // Sub-Account Management
  VIEW_SUB_ACCOUNTS: 'sub_accounts.view',
  CREATE_SUB_ACCOUNT: 'sub_accounts.create',
  UPDATE_SUB_ACCOUNT: 'sub_accounts.update',
  DELETE_SUB_ACCOUNT: 'sub_accounts.delete',

  // User Management
  VIEW_USERS: 'users.view',
  CREATE_USER: 'users.create',
  UPDATE_USER: 'users.update',
  DELETE_USER: 'users.delete',

  // Role Management
  VIEW_ROLES: 'roles.view',
  CREATE_ROLE: 'roles.create',
  UPDATE_ROLE: 'roles.update',
  DELETE_ROLE: 'roles.delete',

  // Assignment Management
  VIEW_ASSIGNMENTS: 'assignments.view',
  CREATE_ASSIGNMENT: 'assignments.create',
  UPDATE_ASSIGNMENT: 'assignments.update',
  DELETE_ASSIGNMENT: 'assignments.delete',
  EXPIRE_ASSIGNMENT: 'assignments.expire',
  VIEW_MEDIA: 'assignments.view_media',
  DOWNLOAD_MEDIA: 'assignments.download_media',

  // Template/Workflow Management
  VIEW_TEMPLATES: 'templates.view',
  CREATE_TEMPLATE: 'templates.create',
  UPDATE_TEMPLATE: 'templates.update',
  DELETE_TEMPLATE: 'templates.delete',

  // Webhooks
  VIEW_WEBHOOKS: 'webhooks.view',
  CREATE_WEBHOOK: 'webhooks.create',
  UPDATE_WEBHOOK: 'webhooks.update',
  DELETE_WEBHOOK: 'webhooks.delete',

  // Settings
  MANAGE_SETTINGS: 'settings.manage',
};

/**
 * Check if a user has specific permissions.
 * @param {string[]} userPermissions - DB-key permissions from GET /auth/me
 * @param {string[]} requiredPermissions - Values from PERMISSIONS constants above
 * @param {boolean} requireAll - true = must have all; false = any one suffices
 * @returns {boolean}
 */
export const hasPermission = (userPermissions = [], requiredPermissions = [], requireAll = false) => {
  if (!requiredPermissions || requiredPermissions.length === 0) { return true; }
  if (!userPermissions || userPermissions.length === 0) { return false; }
  if (requireAll) { return requiredPermissions.every(p => userPermissions.includes(p)); }
  return requiredPermissions.some(p => userPermissions.includes(p));
};

/**
 * Check if user has view-only access (no create/update/delete permissions)
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} resource - Resource name (e.g., 'users', 'assignments')
 * @returns {boolean}
 */
export const isViewOnly = (userPermissions = [], resource) => {
  const viewPermission = `${resource}.view`;
  const hasView = userPermissions.includes(viewPermission);
  const hasCreate = userPermissions.includes(`${resource}.create`);
  const hasUpdate = userPermissions.includes(`${resource}.update`);
  const hasDelete = userPermissions.includes(`${resource}.delete`);

  return hasView && !hasCreate && !hasUpdate && !hasDelete;
};
